import React, { useState } from "react";
import { ScrollSync, ScrollSyncPane } from "react-scroll-sync";
import { FaUpload } from "react-icons/fa";

const JSONMerge = () => {
  const [json1, setJson1] = useState("");
  const [json2, setJson2] = useState("");
  const [mergedJSON, setMergedJSON] = useState("");
  const [error1, setError1] = useState("");
  const [error2, setError2] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);
  const [mergeFeedback, setMergeFeedback] = useState(false);
  const [showLineNumbers, setShowLineNumbers] = useState(true);

  const exampleData1 = `{
      "name": "Alice",
      "age": 25,
      "city": "New York"
  }`;

  const exampleData2 = `{
      "age": 30,
      "country": "USA",
      "phone": "123-456-7890"
  }`;

  const handleFileUpload = (event, setJson) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setJson(e.target.result);
      };
      reader.readAsText(file);
    }
  };

  const validateJSON = (json, setError) => {
    try {
      JSON.parse(json);
      setError("");
      return true;
    } catch (err) {
      const errorPosition = err.message.match(/position (\d+)/);
      if (errorPosition) {
        const position = parseInt(errorPosition[1], 10);
        const lines = json.substring(0, position).split("\n");
        setError(`Error at line ${lines.length}: ${err.message}`);
      } else {
        setError(`Invalid JSON: ${err.message}`);
      }
      return false;
    }
  };

  const mergeJSON = () => {
    const isJSON1Valid = validateJSON(json1, setError1);
    const isJSON2Valid = validateJSON(json2, setError2);

    if (!isJSON1Valid || !isJSON2Valid) {
      setMergedJSON("");
      setCopySuccess(false);
      return;
    }

    try {
      const obj1 = json1.trim() ? JSON.parse(json1) : {};
      const obj2 = json2.trim() ? JSON.parse(json2) : {};
      const merged = { ...obj1, ...obj2 };
      const formattedMerged = JSON.stringify(merged, null, 2);
      setMergedJSON(formattedMerged);
      setError1("");
      setError2("");
      setCopySuccess(false);

      setMergeFeedback(true);
      setTimeout(() => setMergeFeedback(false), 3000);
    } catch (err) {
      setError1("Unexpected error occurred during merging.");
      setError2("");
      setMergedJSON("");
      setCopySuccess(false);
    }
  };

  const clearInputs = () => {
    setJson1("");
    setJson2("");
    setMergedJSON("");
    setError1("");
    setError2("");
    setCopySuccess(false);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(mergedJSON);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const handleExport = () => {
    const blob = new Blob([mergedJSON], { type: "application/json" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "merged.json";
    link.click();
  };

  const useExampleData = () => {
    setJson1(exampleData1);
    setJson2(exampleData2);
    setMergedJSON("");
    setError1("");
    setError2("");
    setCopySuccess(false);
  };

  const scrollToTop = () => {
    const mergedOutputArea = document.getElementById("mergedOutputArea");
    if (mergedOutputArea) {
      mergedOutputArea.scrollTop = 0;
    }
  };

  const scrollToBottom = () => {
    const mergedOutputArea = document.getElementById("mergedOutputArea");
    if (mergedOutputArea) {
      mergedOutputArea.scrollTop = mergedOutputArea.scrollHeight;
    }
  };

  const renderInputWithLineNumbers = (json, setJson, error, id) => (
    <ScrollSync>
      <div style={{ display: "flex", position: "relative", height: "300px" }}>
        <ScrollSyncPane>
          {showLineNumbers && (
            <div
              style={{
                overflowY: "auto",
                backgroundColor: "#f8f9fa",
                borderRight: "1px solid #ccc",
                textAlign: "right",
                userSelect: "none",
                color: "#6c757d",
                paddingRight: "5px",
                paddingLeft: "5px",
                width: "50px",
                boxSizing: "border-box",
                height: "100%",
              }}
            >
              {json.split("\n").map((_, i) => (
                <div key={i} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                  {i + 1}
                </div>
              ))}
            </div>
          )}
        </ScrollSyncPane>
        <ScrollSyncPane>
          <textarea
            id={id}
            className="form-control"
            style={{
              flex: "1",
              height: "100%",
              resize: "none",
              border: error ? "2px solid red" : "2px solid #ccc",
              borderRadius: "0",
              overflowY: "auto",
              paddingLeft: showLineNumbers ? "0.5em" : "1em",
              fontFamily: "monospace",
            }}
            value={json}
            onChange={(e) => setJson(e.target.value)}
            placeholder="Enter JSON here..."
          />
        </ScrollSyncPane>
        <label
          htmlFor={`upload-${id}`}
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            cursor: "pointer",
          }}
        >
          <FaUpload size={20} />
        </label>
        <input
          type="file"
          id={`upload-${id}`}
          style={{ display: "none" }}
          accept=".json"
          onChange={(e) => handleFileUpload(e, setJson)}
        />
      </div>
    </ScrollSync>
  );

  return (
    <div className="container mt-4">
      <div className="text-center mb-3">
        <button className="btn btn-info" onClick={useExampleData}>
          Use Example Data
        </button>
      </div>
      <div className="row">
        <div className="col-md-6 mb-3">
          {renderInputWithLineNumbers(json1, setJson1, error1, "json1")}
          {error1 && <div className="text-danger mt-2">{error1}</div>}
        </div>
        <div className="col-md-6 mb-3">
          {renderInputWithLineNumbers(json2, setJson2, error2, "json2")}
          {error2 && <div className="text-danger mt-2">{error2}</div>}
        </div>
      </div>
      <div className="text-center mb-3">
        <button className="btn btn-success me-2" onClick={mergeJSON}>
          Merge JSON
        </button>
        <button className="btn btn-secondary me-2" onClick={clearInputs}>
          Clear
        </button>
        <label>
          <input
            type="checkbox"
            checked={showLineNumbers}
            onChange={(e) => setShowLineNumbers(e.target.checked)}
            className="me-2"
          />
          Show Line Numbers
        </label>
      </div>
      {mergeFeedback && (
        <div className="text-center text-success mb-3">
          Merging completed successfully!
        </div>
      )}
      {mergedJSON && (
        <div className="mt-4">
          <h5>Merged JSON</h5>
          <ScrollSync>
            <div
              id="mergedOutputArea"
              style={{
                display: "flex",
                position: "relative",
                height: "300px",
                overflow: "hidden",
                backgroundColor: "#f8f9fa",
                border: "2px solid #ccc",
                borderRadius: "5px",
              }}
            >
              <ScrollSyncPane>
                {showLineNumbers && (
                  <div
                    style={{
                      overflowY: "auto",
                      backgroundColor: "#f8f9fa",
                      borderRight: "1px solid #ccc",
                      textAlign: "right",
                      userSelect: "none",
                      color: "#6c757d",
                      padding: "10px",
                      width: "50px",
                      boxSizing: "border-box",
                    }}
                  >
                    {mergedJSON.split("\n").map((_, i) => (
                      <div key={i} style={{ height: "1.5em", lineHeight: "1.5em" }}>
                        {String(i + 1).padStart(2, " ")}
                      </div>
                    ))}
                  </div>
                )}
              </ScrollSyncPane>
              <ScrollSyncPane>
                <pre
                  style={{
                    margin: 0,
                    padding: "10px",
                    overflowY: "auto",
                    overflowX: "auto",
                    whiteSpace: "pre-wrap",
                    flex: 1,
                    fontFamily: "monospace",
                    resize: "vertical", // Allows resizing
                    minHeight: "150px", // Ensures usability
                  }}
                >
                  {mergedJSON}
                </pre>
              </ScrollSyncPane>
            </div>
          </ScrollSync>
          <div className="text-center mt-2">
            <button className="btn btn-secondary me-2" onClick={scrollToTop}>
              Scroll to Top
            </button>
            <button className="btn btn-secondary me-2" onClick={scrollToBottom}>
              Scroll to Bottom
            </button>
            <button
              className={`btn ${copySuccess ? "btn-success" : "btn-primary"}`}
              onClick={handleCopy}
            >
              {copySuccess ? "Copied!" : "Copy Output"}
            </button>
            <button className="btn btn-warning ms-2" onClick={handleExport}>
              Export JSON
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default JSONMerge;